import { STORAGE_KEY } from '@/constant/constant';
import { formEventV2 } from '@/service/formEventV2';
import { getLocal, getSession, setLocal, setSession, removeSession } from '@/utils/storage';
import { getLocalDateString, normalizeToLocalDateString } from '@/utils/dateFormat';
import { safeJsonParse } from '@/utils/safeJsonParse';
import { popupFlowLog } from '@/utils/popupFlowDebug';
const debug = require('debug')('cartsee:priority');


/** localStorage 中 _cartsee_stat 的存储结构，用于 safeJsonParse 类型安全 */
interface StoredPriority {
  popCount?: number;
  date?: string;
  lastTime?: number;
  popLastTimes?: Record<string, number>;
  iconCount?: number;
  iconDate?: string;
  iconLastTimes?: Record<string, number>;
  loadList0?: unknown[];
  lastIconCloseTime?: number;
}

const getResolvedFormGlobalSetting = (popConfig: any) => {
  if (popConfig?.formGlobalSetting) {
    return popConfig.formGlobalSetting;
  }

  const popupConfig = safeJsonParse<any>(getSession(STORAGE_KEY.POPUP) ?? null, {} as any);
  return popupConfig?.formGlobalSetting || popupConfig?.globalSetting;
};

// 打断器，主要用于优先级配置等
class PopPriority {
  /**
   * 当前弹窗个数
   */
  popCount: number;

  /**
   * 全局上限时间
   */
  date: string;

  /**
   * 前一个弹窗时间
   */
  lastTime: number;

  /**
   * 同弹窗上一个弹出时间
   */
  popLastTimes: Record<string, number>;

  /**
   * 当前 Icon 展示个数
   */
  iconCount: number;

  /**
   * 当前 Icon 展示计数日期
   */
  iconDate: string;

  /**
   * 同 Icon 上一次关闭时间
   */
  iconLastTimes: Record<string, number>;

  /**
   * 新手订阅曝光日期列表
   */
  loadList0: string[];

  /**
   * 关闭 Icon 的时间戳（用于“显示前 Icon”频控对比时间）
   */
  lastIconCloseTime?: number;

  constructor() {
    // 获取存储的配置（safeJsonParse 防止 localStorage 损坏/篡改导致崩溃）
    const priority = safeJsonParse<StoredPriority>(getLocal(STORAGE_KEY.CARTSEE_STAT) ?? null, {});
    // 初始化当前弹窗个数
    this.popCount = priority.popCount || 0;
    // 全局上限时间（兼容存量：旧版可能是 UTC 或其他格式，规范化为本地 YYYY-MM-DD）
    this.date = normalizeToLocalDateString(priority.date) || getLocalDateString();
    // 初始化前一个弹窗时间
    this.lastTime = priority.lastTime || undefined;
    // 初始化同弹窗上一个弹出时间
    this.popLastTimes = priority.popLastTimes || {};
    // 初始化当前 Icon 个数
    this.iconCount = priority.iconCount || 0;
    // 初始化 Icon 上限时间（本地日期）
    this.iconDate = normalizeToLocalDateString(priority.iconDate) || getLocalDateString();
    // 初始化同 Icon 上一次关闭时间
    this.iconLastTimes = priority.iconLastTimes || {};
    // 新手订阅曝光日期列表（兼容存量：规范化并过滤无效项）
    this.loadList0 = this.normalizeLoadList0(priority.loadList0 || []);
    // Icon 关闭时间戳
    this.lastIconCloseTime = priority.lastIconCloseTime || undefined;
    // 更新全局上限
    this.updatePopCount();
    this.updateIconCount();
  }

  updateFromStorage() {
    const priority = safeJsonParse<StoredPriority>(getLocal(STORAGE_KEY.CARTSEE_STAT) ?? null, {});
    // 初始化当前弹窗个数
    this.popCount = priority.popCount || 0;
    // 全局上限时间（兼容存量）
    this.date = normalizeToLocalDateString(priority.date) || getLocalDateString();
    // 初始化前一个弹窗时间
    this.lastTime = priority.lastTime || undefined;
    // 初始化同弹窗上一个弹出时间
    this.popLastTimes = priority.popLastTimes || {};
    // 初始化当前 Icon 个数
    this.iconCount = priority.iconCount || 0;
    // 初始化 Icon 上限时间（兼容存量）
    this.iconDate = normalizeToLocalDateString(priority.iconDate) || getLocalDateString();
    // 初始化同 Icon 上一次关闭时间
    this.iconLastTimes = priority.iconLastTimes || {};
    // 新手订阅曝光日期列表（兼容存量）
    this.loadList0 = this.normalizeLoadList0(priority.loadList0 || []);
    // Icon 关闭时间戳
    this.lastIconCloseTime = priority.lastIconCloseTime || undefined;
    // 更新全局上限
    this.updatePopCount();
    this.updateIconCount();
  }

  /** 将存量 loadList0 规范化为本地 YYYY-MM-DD 数组，无效项过滤掉 */
  private normalizeLoadList0(raw: unknown[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const item of raw) {
      const normalized = normalizeToLocalDateString(item);
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        result.push(normalized);
      }
    }
    return result;
  }

  updatePopCount() {
    // 全局上限重置逻辑
    // 只重置全局上限，全局静默和类型弹窗弹出间隔不做重置
    const priority = safeJsonParse<StoredPriority>(getLocal(STORAGE_KEY.CARTSEE_STAT) ?? null, {});
    const current = getLocalDateString();
    // 存量 date 可能格式不同，规范化后再比较
    const storedDate = normalizeToLocalDateString(priority.date);
    if (storedDate !== current) {
      // 如果全局上限日期与当前本地日期不相等（或存量格式无效），则重置
      this.popCount = 0;
      this.date = current;
      this.updateStorage();
    }
  }

  updateIconCount() {
    const priority = safeJsonParse<StoredPriority>(getLocal(STORAGE_KEY.CARTSEE_STAT) ?? null, {});
    const current = getLocalDateString();
    const storedDate = normalizeToLocalDateString(priority.iconDate);
    if (storedDate !== current) {
      this.iconCount = 0;
      this.iconDate = current;
      this.updateStorage();
    }
  }
  updateStorage() {
    setLocal(
      STORAGE_KEY.CARTSEE_STAT,
      JSON.stringify({
        popCount: this.popCount,
        lastTime: this.lastTime,
        popLastTimes: this.popLastTimes,
        iconCount: this.iconCount,
        iconDate: this.iconDate,
        iconLastTimes: this.iconLastTimes,
        date: this.date,
        loadList0: this.loadList0,
        lastIconCloseTime: this.lastIconCloseTime
      })
    );
  }

  setIconCloseTime(popId?: string, time = new Date().getTime()) {
    this.updateIconCount();
    if (popId) {
      this.iconLastTimes[popId] = time;
    }
    this.iconCount += 1;
    this.lastIconCloseTime = time;
    this.updateStorage();
  }
  // sessionStorage增加allPopCount用于判断当前会话已弹弹窗数量
  updateAllPopCount() {
    const sessionAllPop = getSession(STORAGE_KEY.ALL_POP_COUNT);
    if (!sessionAllPop) {
      setSession(STORAGE_KEY.ALL_POP_COUNT, '1');
    } else {
      setSession(STORAGE_KEY.ALL_POP_COUNT, Number(sessionAllPop) + 1 + '');
    }
  }
  // sessionStorage增加leavePopCount用于判断当前会话已弹-退出挽留弹窗-弹窗数量
  updateLeavePopCount() {
    const sessionLeavePop = getSession(STORAGE_KEY.LEAVE_POP_COUNT);
    if (!sessionLeavePop) {
      setSession(STORAGE_KEY.LEAVE_POP_COUNT, '1');
    } else {
      setSession(STORAGE_KEY.LEAVE_POP_COUNT, Number(sessionLeavePop) + 1 + '');
    }
  }
  clearLoadList0() {
    this.loadList0 = [];
    this.updateStorage();
    this.updateAllPopCount();
  }
  checkPop0Show() {
    const N = 7;
    const X = 2;
    if (this.loadList0 && this.loadList0.length >= X) {
      const currentDate = new Date();
      const currentDateStr = getLocalDateString(currentDate);
      const oldDate = new Date(currentDate.getTime() - N * 24 * 3600 * 1000);
      const oldDateStr = getLocalDateString(oldDate);
      // 存量 loadList0 已在读取时规范化，这里用字符串比较即可（YYYY-MM-DD 字典序即日期序）
      const list = this.loadList0.filter(
        (item) => item !== currentDateStr && item >= oldDateStr
      );
      if (list.length >= X) {
        return false;
      }
    }
    return true;
  }
  popAType(type, popId = undefined) {
    // 离开意图(1)：单独统计 leavePopCount，用于频率/上限控制
    if (type === 1) {
      this.updateLeavePopCount();
    }
    // 毫秒的时间戳
    const current = new Date().getTime();
    if (!!popId) {
      this.popLastTimes[popId] = current;
    }
    if (`${type}` === '0') {
      // 记录新手订阅类型曝光日期
      const date = getLocalDateString();
      if (!this.loadList0) {
        this.loadList0 = [];
      }
      if (!this.loadList0?.includes(date)) {
        this.loadList0.push(date);
        if (this.loadList0.length > 15) {
          this.loadList0.shift();
        }
      }
    }
    // 记录次数
    this.popCount += 1;
    // 记录时间，用于计算静默
    this.lastTime = current;
    // console.log(this);
    this.updateStorage();
    this.updateAllPopCount();
  }
  // 弹窗展示来源：first/second 用于埋点，有当前展示弹窗时不覆盖
  setPopShowFrom(config) {
    if (config && config.id) {
      const hasShowPop = getSession(STORAGE_KEY.POP_SHOW);
      if (!hasShowPop) {
        if (this.popLastTimes && this.popLastTimes[`${config.id}`]) {
          setLocal(STORAGE_KEY.SHOW_FROM, 'second');
        } else {
          setLocal(STORAGE_KEY.SHOW_FROM, 'first');
        }
      }
      const hasShowFrom = getLocal(STORAGE_KEY.SHOW_FROM);
      if (!hasShowFrom) {
        setLocal(STORAGE_KEY.SHOW_FROM, 'first');
      }
    }
  }
  canPopAType(type, config) {
    // 如果有两次间隔才判断，否则直接弹
    if (config?.settings?.rules?.displayFrequency) {
      // 本地有时间戳，计算间隔
      if (this.popLastTimes[`${config.id}`]) {
        const current = new Date().getTime();
        // console.log('目前时间为', current);
        // console.log('上次弹出时间为', this.popLastTimes[type]);
        // console.log('间隔设置为', config?.settings?.rules?.displayFrequency * 1000);
        // console.log('两次间隔', current - this.popLastTimes[type]);
        try {
          // 放到try catch里
          if (current - this.popLastTimes[`${config.id}`] > config?.settings?.rules?.displayFrequency * 1000) {
            // 不直接记录时间，等待view后在计算显示
            // this.popAType(type);
            // 设置弹窗从何而来
            this.setPopShowFrom(config);
            popupFlowLog(
              'priority.canPopAType pass interval type=%s popId=%s diffMs=%s needMs=%s',
              type,
              config?.id,
              current - this.popLastTimes[`${config.id}`],
              config?.settings?.rules?.displayFrequency * 1000
            );
            return true;
          }
        } catch (error) {}
        debug('canPopAType type=%d current=%d displayFrequency=%d', type, current - this.popLastTimes[`${config.id}`], config?.settings?.rules?.displayFrequency * 1000);
        popupFlowLog(
          'priority.canPopAType block interval type=%s popId=%s diffMs=%s needMs=%s (popup_h5_result 间隔时间)',
          type,
          config?.id,
          current - this.popLastTimes[`${config.id}`],
          config?.settings?.rules?.displayFrequency * 1000
        );
        // 不通过
        formEventV2({
          event: 'popup_h5_result',
          triggerEventType: config?.triggerEventType,
          formId: config?.id,
          formName: config?.name,
          result: '间隔时间'
        })
          .then((response) => {
            // console.log('数据上报成功')
          })
          .catch((e) => {});
        // console.log('间隔时间');
        if (`${type}` === '2') {
          // 清除加购标记
          removeSession(STORAGE_KEY.ADD_CART_STATE);
        }
        return false;
      } else {
        // 本地没有时间戳，直接弹
        // 不直接记录时间，等待view后在计算显示
        // this.popAType(type);
        // 设置弹窗从何而来
        this.setPopShowFrom(config);
        popupFlowLog('priority.canPopAType pass first_or_no_last_time type=%s popId=%s', type, config?.id);
        return true;
      }
    }
    // 没有间隔
    // 不直接记录时间，等待view后在计算显示
    // this.popAType(type);
    // 设置弹窗从何而来
    this.setPopShowFrom(config);
    popupFlowLog('priority.canPopAType pass no_displayFrequency_rule type=%s popId=%s', type, config?.id);
    return true;
  }
  // 判断全局上限、全局静默
  canShowPop(config) {
    // 更新全局上限
    this.updatePopCount();
    // 有全局配置
    if (config?.formGlobalSetting) {
      // console.log('全局上限', config?.formGlobalSetting?.maxTrigger);
      // console.log('当前个数', this.popCount);
      let triggerLogic = false;
      // 获取全局上限，最小为1个
      const triggerCount = config?.formGlobalSetting?.maxTrigger || 1;
      if (this.popCount < triggerCount) {
        triggerLogic = true;
      }
      if (!triggerLogic) {
        popupFlowLog('priority.canShowPop block global maxTrigger popCount=%s maxTrigger=%s', this.popCount, triggerCount);
        // 不能超过全局上限
        formEventV2({
          event: 'popup_h5_result',
          // 同时触发多个type，不报了
          // triggerEventType: config?.triggerEventType,
          // formId: config?.id,
          // formName: config?.name,
          result: '全局上限'
        })
          .then((response) => {
            // console.log('数据上报成功')
          })
          .catch((e) => {});
        // console.log('全局上限');
        // 直接清除加购标记
        removeSession(STORAGE_KEY.ADD_CART_STATE);
        return false;
      }

      let quiescent = false;
      // 当前时间
      // console.log('全局静默状态', config?.formGlobalSetting?.quiescentState);
      // console.log('全局静默时间', config?.formGlobalSetting?.quiescentPeriod * 1000);
      // 是否开启全局静默
      if (`${config?.formGlobalSetting?.quiescentState}` === '1') {
        // 如果有上一个弹窗
        if (this.lastTime) {
          const current = new Date().getTime();
          // console.log('当前时间', current);
          // console.log('上次弹出时间', this.lastTime);
          // console.log('目前的静默间隔', current - this.lastTime);
          // 获取全局静默时间，最小为1秒
          const numb = config?.formGlobalSetting?.quiescentPeriod || 1;
          // 判断时间是否超过静默期
          if (current - this.lastTime > numb * 1000) {
            quiescent = true;
          }
        } else {
          // 如果没有弹过，直接弹
          quiescent = true;
        }
      } else {
        // 如果没有静默，直接弹
        quiescent = true;
      }

      if (triggerLogic && quiescent) {
        // 全局上限 + 全局静默 均符合条件，弹出
        // console.log('全局上限+全局静默符合条件');
        popupFlowLog(
          'priority.canShowPop pass global limits popCount=%s maxTrigger=%s quiescent=%s',
          this.popCount,
          triggerCount,
          quiescent
        );
        return true;
      } else {
        popupFlowLog(
          'priority.canShowPop block quiescent triggerLogic=%s quiescent=%s popCount=%s lastTime=%s',
          triggerLogic,
          quiescent,
          this.popCount,
          this.lastTime
        );
        formEventV2({
          event: 'popup_h5_result',
          // 同时触发多个type，不报了
          // triggerEventType: config?.triggerEventType,
          // formId: config?.id,
          // formName: config?.name,
          result: '全局静默期'
        })
          .then((response) => {
            // console.log('数据上报成功')
          })
          .catch((e) => {});
        // console.log('全局静默期');
        // 直接清除加购标记
        removeSession(STORAGE_KEY.ADD_CART_STATE);
        return false;
      }
    } else {
      // 没有全局配置，直接弹
      return true;
    }
  }
}

const popPriority = new PopPriority();

// 判断全局上限 + 全局静默
const canShowAnyPop = (config) => {
  return popPriority.canShowPop(config);
};

// 两次间隔判断
const canShowThisPop = (popConfig, popType) => {
  return popPriority.canPopAType(popType, popConfig);
};

// 真正view之后再记录两次间隔，全局上限
const showAPop = (popType, popId = undefined) => {
  return popPriority.popAType(popType, popId);
};

const submitAPop = (popType) => {
  popPriority.clearLoadList0();
};

const checkPopShow = () => {
  return popPriority.checkPop0Show();
};

// 从本次存储更新，解决窗口状态同步
const updateFromStorage = () => {
  return popPriority.updateFromStorage();
};

/**
 * 判定「显示前 icon」是否允许展示（联动关闭 icon 时间）
 *
 * 规则说明：
 * - icon 复用弹窗的全局次数 / 全局静默 / 单弹窗间隔配置
 * - 但状态完全独立：使用 iconCount / lastIconCloseTime / iconLastTimes
 * - 关闭 icon 时才更新 icon 自己的计数与时间轴，不影响弹窗曝光状态
 */
const canShowIconBeforeByFrequency = (popConfig: any) => {
  popPriority.updateIconCount();
  const current = new Date().getTime();

  const globalSetting = getResolvedFormGlobalSetting(popConfig);
  let globalAllowed = true;
  if (globalSetting) {
    // icon 独立走自己的全局上限和静默期，不影响弹窗曝光状态
    const triggerCount = globalSetting?.maxTrigger || 1;
    const triggerLogic = popPriority.iconCount < triggerCount;
    if (!triggerLogic) return false;

    let quiescent = true;
    if (`${globalSetting?.quiescentState}` === '1') {
      const numb = globalSetting?.quiescentPeriod || 1;
      const referenceTimeGlobal = popPriority.lastIconCloseTime || 0;
      if (referenceTimeGlobal) {
        quiescent = current - referenceTimeGlobal > numb * 1000;
      } else {
        quiescent = true;
      }
    }
    globalAllowed = quiescent;
    if (!globalAllowed) return false;
  }

  // 单弹窗频次逻辑
  const displayFrequency = popConfig?.settings?.rules?.displayFrequency;
  if (displayFrequency) {
    const popId = `${popConfig?.id}`;
    const lastIconClose = popPriority.iconLastTimes?.[popId];
    const referenceTimeSingle = lastIconClose || 0;
    if (referenceTimeSingle) {
      const allowed = current - referenceTimeSingle > displayFrequency * 1000;
      if (!allowed) return false;
    }
  }

  return true;
};

const setIconCloseTime = (popId?: string, time = new Date().getTime()) => {
  popPriority.setIconCloseTime(popId, time);
};

export {
  canShowAnyPop,
  canShowThisPop,
  showAPop,
  submitAPop,
  checkPopShow,
  updateFromStorage,
  popPriority,
  canShowIconBeforeByFrequency,
  setIconCloseTime
};
